import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, ArrowRight } from "lucide-react";
import { askCopilot, copilotStreamUrl } from "../../api";

const CopilotDrawer = ({ open, onClose, entity, onNavigate }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const sessionId = useRef(`sess_${Math.random().toString(36).slice(2, 12)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  const patchLast = (patch) =>
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], ...patch };
      return next;
    });

  const streamReply = async (text) => {
    const res = await fetch(copilotStreamUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("tf_token") || ""}`,
      },
      body: JSON.stringify({ session_id: sessionId.current, message: text }),
    });
    if (!res.ok || !res.body) throw new Error("stream failed");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let started = false;
    let content = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        if (!part.startsWith("data: ")) continue;
        const ev = JSON.parse(part.slice(6));
        if (ev.type === "delta") {
          if (!started) {
            started = true;
            setTyping(false);
            setMessages((prev) => [
              ...prev,
              { role: "assistant", variant: "recommendation", title: "Copilot:", content: "", action: null },
            ]);
          }
          content += ev.text;
          patchLast({ content });
        } else if (ev.type === "done") {
          if (!started) {
            started = true;
            setTyping(false);
            setMessages((prev) => [
              ...prev,
              { role: "assistant", variant: "recommendation", title: "Copilot:", content: "", action: null },
            ]);
          }
          patchLast({ content: ev.reply, action: ev.action });
        } else if (ev.type === "error") {
          throw new Error(ev.detail || "copilot error");
        }
      }
    }
    if (!started) throw new Error("empty stream");
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || typing) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setTyping(true);
    try {
      await streamReply(text);
    } catch {
      try {
        const res = await askCopilot(sessionId.current, text);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", variant: "recommendation", title: "Engine Answer:", content: res.reply, action: res.action },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", variant: "plain", content: "The engine is unreachable right now — please retry." },
        ]);
      }
    }
    setTyping(false);
  };

  return (
    <div
      data-testid="copilot-drawer"
      className={`fixed inset-y-0 right-0 w-96 glass-panel border-l border-white/10 z-50 flex flex-col transform transition-transform duration-300 shadow-2xl ${
        open ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="p-4 border-b border-white/10 flex items-center justify-between bg-zinc-950/70">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-4 h-4 text-blue-400" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-white">PnLFlow Copilot</h3>
          <span className="px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300 text-[9px] font-bold uppercase">
            Claude
          </span>
        </div>
        <button data-testid="copilot-close-btn" onClick={onClose} className="text-zinc-500 hover:text-zinc-200 transition">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 p-4 overflow-y-auto custom-scrollbar space-y-3 text-xs" data-testid="copilot-messages">
        <div className="bg-white/5 p-3 rounded-md border border-white/10 text-zinc-400 leading-relaxed">
          Hello! I'm the AI copilot for <strong className="text-white">{entity}</strong> — powered by Claude and
          grounded in the live engagement data computed by the engine. Ask me about revenue, CapEx, integrity controls,
          anomalies, materiality, billing or the audit chain.
        </div>
        {messages.map((msg, i) => {
          if (msg.role === "user") {
            return (
              <div key={i} className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-md text-zinc-200 leading-relaxed ml-8">
                {msg.content}
              </div>
            );
          }
          if (msg.variant === "recommendation") {
            return (
              <div key={i} className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-md text-blue-100 leading-relaxed whitespace-pre-wrap">
                <strong className="block text-blue-400 font-bold mb-1">{msg.title}</strong>
                {msg.content}
                {msg.action && (
                  <button
                    data-testid={`copilot-action-btn-${i}`}
                    onClick={() => onNavigate(msg.action.view)}
                    className="mt-2 w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-semibold rounded-md transition flex items-center justify-center space-x-1"
                  >
                    <span>{msg.action.label}</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          }
          return (
            <div key={i} className="bg-white/5 p-3 rounded-md border border-white/10 text-zinc-400 leading-relaxed">
              {msg.content}
            </div>
          );
        })}
        {typing && (
          <div className="bg-white/5 p-3 rounded-md border border-white/10 text-zinc-500 flex items-center space-x-1" data-testid="copilot-typing">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-white/10 bg-zinc-950/70">
        <div className="relative">
          <input
            type="text"
            data-testid="copilot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Ask about revenue, CapEx, controls, anomalies..."
            className="w-full bg-zinc-900 border border-white/20 rounded-md pl-3 pr-10 py-2 text-xs text-zinc-300 focus:outline-none focus:border-blue-500"
          />
          <button
            data-testid="copilot-send-btn"
            onClick={sendMessage}
            className="absolute right-2 top-2 text-blue-400 hover:text-blue-400 transition"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopilotDrawer;
